import {
  Badge,
  Button,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  cn,
  formatDate,
  formatDateTime,
  formatRelative,
} from '@lawbee/ui';
import { CalendarClock, Check, Download, FileText, ListChecks, NotebookPen, Plus, Trash2, Upload, Users } from 'lucide-react';
import { useRef, useState, type FormEvent } from 'react';
import { api, apiUrl } from '../../../lib/api';
import { formatBytes, todayIn, useApiMutation } from '../../../lib/hooks';
import { useMe } from '../../../lib/me';
import type { MatterDetail } from '../../../lib/types';
import { DEADLINE_KINDS, MATTER_PARTY_ROLES, Panel, deadlineKindLabel, fullName, roleLabel, useMatterInvalidate, useTeam } from './shared';

// ---------------------------------------------------------------------------
// Parties
// ---------------------------------------------------------------------------

export function PartiesTab({ data }: { data: MatterDetail }) {
  const id = data.matter.id;
  const invalidate = useMatterInvalidate(id);
  const [name, setName] = useState('');
  const [role, setRole] = useState('opposing_party');
  const [removing, setRemoving] = useState<{ id: string; name: string } | null>(null);
  const add = useApiMutation((body: { name: string; role: string }) => api.post(`/api/matters/${id}/parties`, body), {
    success: 'Party added',
    onSuccess: () => {
      setName('');
      void invalidate();
    },
  });
  const remove = useApiMutation((partyId: string) => api.del(`/api/matters/${id}/parties/${partyId}`), {
    success: 'Party removed',
    onSuccess: () => {
      setRemoving(null);
      void invalidate();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim()) add.mutate({ name: name.trim(), role });
  };
  const client = data.matter.client;
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
      <Panel title="Parties" description="Everyone involved. Names here are searched in future conflict checks.">
        <ul className="divide-y divide-border/70">
          {client ? (
            <li className="flex items-center justify-between gap-3 py-2.5 first:pt-0">
              <span className="min-w-0">
                <span className="block truncate font-medium">{fullName(client.firstName, client.lastName)}</span>
                <span className="text-[12px] text-muted-foreground">Client</span>
              </span>
            </li>
          ) : null}
          {data.parties.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-3 py-2.5 last:pb-0">
              <span className="min-w-0">
                <span className="block truncate font-medium">{p.name}</span>
                <span className="text-[12px] text-muted-foreground">{roleLabel(p.role)}</span>
              </span>
              <Button variant="ghost" size="icon" className="size-8 text-destructive" aria-label={`Remove ${p.name}`} onClick={() => setRemoving({ id: p.id, name: p.name })}>
                <Trash2 />
              </Button>
            </li>
          ))}
        </ul>
        {data.parties.length === 0 ? (
          <EmptyState className="py-8" icon={<Users />} title="No other parties" description="Add the other side, opposing counsel and anyone else involved." />
        ) : null}
      </Panel>
      <Panel title="Add a party">
        <form className="space-y-4" onSubmit={submit}>
          <Field label="Name" htmlFor="party-name" required>
            <Input id="party-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name or company" />
          </Field>
          <Field label="Role" htmlFor="party-role">
            <Select id="party-role" value={role} onChange={(e) => setRole(e.target.value)} options={MATTER_PARTY_ROLES} />
          </Field>
          <div className="flex justify-end">
            <Button type="submit" loading={add.isPending} disabled={!name.trim()}>
              <Plus aria-hidden /> Add party
            </Button>
          </div>
        </form>
      </Panel>
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={`Remove ${removing?.name ?? 'party'}?`}
        description="They will no longer appear in conflict searches for this matter."
        confirmLabel="Remove"
        destructive
        loading={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing.id)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Deadlines
// ---------------------------------------------------------------------------

export function DeadlinesTab({ data }: { data: MatterDetail }) {
  const me = useMe();
  const id = data.matter.id;
  const today = todayIn(me.workspace.timezone);
  const invalidate = useMatterInvalidate(id);
  const [v, setV] = useState({ title: '', dueOn: '', kind: 'court', clientVisible: false });
  const [deleting, setDeleting] = useState<{ id: string; title: string } | null>(null);
  const add = useApiMutation(() => api.post(`/api/matters/${id}/deadlines`, { title: v.title.trim(), dueOn: v.dueOn, kind: v.kind, clientVisible: v.clientVisible }), {
    success: 'Deadline added',
    onSuccess: () => {
      setV({ title: '', dueOn: '', kind: v.kind, clientVisible: false });
      void invalidate();
    },
  });
  const patch = useApiMutation((args: { did: string; status: 'open' | 'done' }) => api.patch(`/api/matters/${id}/deadlines/${args.did}`, { status: args.status }), {
    success: (_d) => 'Deadline updated',
    onSuccess: () => void invalidate(),
  });
  const del = useApiMutation((did: string) => api.del(`/api/matters/${id}/deadlines/${did}`), {
    success: 'Deadline deleted',
    onSuccess: () => {
      setDeleting(null);
      void invalidate();
    },
  });
  const sorted = [...data.deadlines].sort((a, b) => Number(a.status === 'done') - Number(b.status === 'done') || a.dueOn.localeCompare(b.dueOn));

  return (
    <div className="space-y-6">
      <Panel title="Deadlines" contentClassName="px-0 sm:px-6">
        {sorted.length === 0 ? (
          <EmptyState className="py-8" icon={<CalendarClock />} title="No deadlines" description="Add court dates, filing deadlines and limitation periods. Client-visible deadlines appear in the client portal." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Deadline</TableHead>
                <TableHead>Due</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Client sees</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((d) => {
                const done = d.status === 'done';
                const overdue = !done && d.dueOn < today;
                return (
                  <TableRow key={d.id} className={cn(done && 'text-muted-foreground')}>
                    <TableCell className={cn('min-w-40 font-medium', done && 'line-through')}>{d.title}</TableCell>
                    <TableCell className={cn('figures whitespace-nowrap', overdue && 'text-destructive')}>{formatDate(d.dueOn)}</TableCell>
                    <TableCell className="whitespace-nowrap">{deadlineKindLabel(d.kind)}</TableCell>
                    <TableCell>{d.clientVisible ? <Badge variant="info">Visible</Badge> : <span className="text-muted-foreground">No</span>}</TableCell>
                    <TableCell>{done ? <Badge variant="success">Done</Badge> : overdue ? <Badge variant="destructive">Overdue</Badge> : <Badge>Open</Badge>}</TableCell>
                    <TableCell className="whitespace-nowrap text-right">
                      <Button size="sm" variant="ghost" onClick={() => patch.mutate({ did: d.id, status: done ? 'open' : 'done' })} disabled={patch.isPending}>
                        {done ? 'Reopen' : (
                          <>
                            <Check aria-hidden /> Done
                          </>
                        )}
                      </Button>
                      <Button size="icon" variant="ghost" className="size-8 text-destructive" aria-label={`Delete ${d.title}`} onClick={() => setDeleting({ id: d.id, title: d.title })}>
                        <Trash2 />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Panel>
      <Panel title="Add a deadline">
        <form
          className="grid gap-4 sm:grid-cols-2 lg:grid-cols-[2fr_1fr_1fr_auto] lg:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            if (v.title.trim() && v.dueOn) add.mutate(undefined);
          }}
        >
          <Field label="Title" htmlFor="dl-title" required className="sm:col-span-2 lg:col-span-1">
            <Input id="dl-title" value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} placeholder="Case conference" />
          </Field>
          <Field label="Due" htmlFor="dl-due" required>
            <Input id="dl-due" type="date" value={v.dueOn} onChange={(e) => setV({ ...v, dueOn: e.target.value })} className="figures" />
          </Field>
          <Field label="Kind" htmlFor="dl-kind">
            <Select id="dl-kind" value={v.kind} onChange={(e) => setV({ ...v, kind: e.target.value })} options={DEADLINE_KINDS} />
          </Field>
          <Button type="submit" loading={add.isPending} disabled={!v.title.trim() || !v.dueOn}>
            <Plus aria-hidden /> Add
          </Button>
          <div className="sm:col-span-2 lg:col-span-4">
            <Checkbox label="Show this deadline to the client in their portal" checked={v.clientVisible} onCheckedChange={(c) => setV({ ...v, clientVisible: c === true })} />
          </div>
        </form>
      </Panel>
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete this deadline?"
        description={deleting?.title}
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={() => deleting && del.mutate(deleting.id)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tasks
// ---------------------------------------------------------------------------

export function TasksTab({ data }: { data: MatterDetail }) {
  const me = useMe();
  const id = data.matter.id;
  const today = todayIn(me.workspace.timezone);
  const team = useTeam();
  const invalidate = useMatterInvalidate(id);
  const [v, setV] = useState({ title: '', dueOn: '', assigneeUserId: '' });
  const add = useApiMutation(() => api.post(`/api/matters/${id}/tasks`, { title: v.title.trim(), dueOn: v.dueOn || null, assigneeUserId: v.assigneeUserId || null }), {
    success: 'Task added',
    onSuccess: () => {
      setV({ title: '', dueOn: '', assigneeUserId: v.assigneeUserId });
      void invalidate();
    },
  });
  const toggle = useApiMutation((args: { tid: string; status: 'open' | 'done' }) => api.patch(`/api/matters/${id}/tasks/${args.tid}`, { status: args.status }), {
    onSuccess: () => void invalidate(),
  });
  const open = data.tasks.filter((t) => t.status !== 'done');
  const done = data.tasks.filter((t) => t.status === 'done');
  const memberName = (userId: string | null) => (userId ? team.data?.members.find((m) => m.userId === userId)?.name : undefined);

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
      <Panel title="Tasks" description={`${open.length} open`}>
        {data.tasks.length === 0 ? (
          <EmptyState className="py-8" icon={<ListChecks />} title="No tasks" description="Keep the file moving: add the next things to do and who does them." />
        ) : (
          <ul className="divide-y divide-border/70">
            {[...open, ...done].map((t) => {
              const isDone = t.status === 'done';
              const assignee = t.assigneeName ?? memberName(t.assigneeUserId);
              return (
                <li key={t.id} className="flex items-start gap-3 py-2.5 first:pt-0 last:pb-0">
                  <Checkbox
                    className="mt-0.5"
                    aria-label={isDone ? `Mark "${t.title}" as not done` : `Mark "${t.title}" as done`}
                    checked={isDone}
                    disabled={toggle.isPending && toggle.variables?.tid === t.id}
                    onCheckedChange={(c) => toggle.mutate({ tid: t.id, status: c === true ? 'done' : 'open' })}
                  />
                  <div className="min-w-0 flex-1">
                    <p className={cn('text-sm', isDone && 'text-muted-foreground line-through')}>{t.title}</p>
                    <p className="text-[12px] text-muted-foreground">
                      {assignee ?? 'Unassigned'}
                      {t.dueOn ? (
                        <>
                          {' · '}
                          <span className={cn('figures', !isDone && t.dueOn < today && 'text-destructive')}>Due {formatDate(t.dueOn)}</span>
                        </>
                      ) : null}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
      <Panel title="Add a task">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (v.title.trim()) add.mutate(undefined);
          }}
        >
          <Field label="Task" htmlFor="task-title" required>
            <Input id="task-title" value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} placeholder="Draft financial statement" />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            <Field label="Due" htmlFor="task-due">
              <Input id="task-due" type="date" className="figures" value={v.dueOn} onChange={(e) => setV({ ...v, dueOn: e.target.value })} />
            </Field>
            <Field label="Assignee" htmlFor="task-assignee">
              <Select
                id="task-assignee"
                value={v.assigneeUserId}
                onChange={(e) => setV({ ...v, assigneeUserId: e.target.value })}
                options={[{ value: '', label: 'Unassigned' }, ...(team.data?.members ?? []).map((m) => ({ value: m.userId, label: m.name || m.email }))]}
              />
            </Field>
          </div>
          <div className="flex justify-end">
            <Button type="submit" loading={add.isPending} disabled={!v.title.trim()}>
              <Plus aria-hidden /> Add task
            </Button>
          </div>
        </form>
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Notes
// ---------------------------------------------------------------------------

export function NotesTab({ data }: { data: MatterDetail }) {
  const id = data.matter.id;
  const invalidate = useMatterInvalidate(id);
  const [body, setBody] = useState('');
  const add = useApiMutation(() => api.post(`/api/matters/${id}/notes`, { body: body.trim() }), {
    success: 'Note added',
    onSuccess: () => {
      setBody('');
      void invalidate();
    },
  });
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Panel title="Add a note" description="Internal only. Clients never see notes.">
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (body.trim()) add.mutate(undefined);
          }}
        >
          <Textarea aria-label="Note" rows={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Spoke with the client about…" />
          <div className="flex justify-end">
            <Button type="submit" loading={add.isPending} disabled={!body.trim()}>
              Add note
            </Button>
          </div>
        </form>
      </Panel>
      {data.notes.length === 0 ? (
        <EmptyState icon={<NotebookPen />} title="No notes yet" description="Notes help the next person who opens this file." />
      ) : (
        <ol className="space-y-3">
          {data.notes.map((n) => (
            <li key={n.id} className="rounded-xl bg-card p-4 shadow-card">
              <p className="mb-2 flex flex-wrap items-center justify-between gap-2 text-[12px] text-muted-foreground">
                <span className="font-medium text-foreground">{n.authorName ?? 'Someone'}</span>
                <span className="figures" title={formatDateTime(n.createdAt)}>
                  {formatRelative(n.createdAt)}
                </span>
              </p>
              <p className="whitespace-pre-line text-sm leading-relaxed">{n.body}</p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

function uploaderLabel(uploadedBy: string | null, me: { user: { id: string; name: string } }): string {
  if (!uploadedBy) return 'Unknown';
  if (uploadedBy === 'intake') return 'From intake';
  if (uploadedBy === 'client' || uploadedBy.startsWith('client') || uploadedBy.startsWith('contact:')) return 'Client';
  if (uploadedBy === `user:${me.user.id}`) return 'You';
  if (uploadedBy.startsWith('user:')) return 'Team member';
  return uploadedBy;
}

export function DocumentsTab({ data }: { data: MatterDetail }) {
  const me = useMe();
  const id = data.matter.id;
  const invalidate = useMatterInvalidate(id);
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [clientVisible, setClientVisible] = useState(false);
  const [deleting, setDeleting] = useState<{ id: string; name: string } | null>(null);
  const upload = useApiMutation(
    () => {
      const fd = new FormData();
      if (file) fd.set('file', file, file.name);
      fd.set('clientVisible', clientVisible ? 'true' : 'false');
      return api.upload(`/api/matters/${id}/documents`, fd);
    },
    {
      success: 'Document uploaded',
      errorTitle: 'Upload failed',
      onSuccess: () => {
        setFile(null);
        setClientVisible(false);
        if (fileRef.current) fileRef.current.value = '';
        void invalidate();
      },
    },
  );
  const del = useApiMutation((docId: string) => api.del(`/api/matters/${id}/documents/${docId}`), {
    success: 'Document deleted',
    onSuccess: () => {
      setDeleting(null);
      void invalidate();
    },
  });
  return (
    <div className="space-y-6">
      <Panel title="Upload a document">
        <form
          className="flex flex-col gap-4 lg:flex-row lg:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            if (file) upload.mutate(undefined);
          }}
        >
          <Field label="File" htmlFor="doc-file" className="lg:flex-1">
            <input
              ref={fileRef}
              id="doc-file"
              type="file"
              className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-lg file:border-0 file:bg-secondary file:px-3 file:py-2 file:text-sm file:font-medium file:text-secondary-foreground hover:file:bg-foreground/[0.08]"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </Field>
          <Checkbox label="Client can see it in the portal" checked={clientVisible} onCheckedChange={(c) => setClientVisible(c === true)} />
          <Button type="submit" loading={upload.isPending} disabled={!file}>
            <Upload aria-hidden /> Upload
          </Button>
        </form>
      </Panel>
      <Panel title="Documents" contentClassName="px-0 sm:px-6">
        {data.documents.length === 0 ? (
          <EmptyState className="py-8" icon={<FileText />} title="No documents" description="Upload pleadings, agreements and correspondence. Intake attachments appear here after conversion." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Size</TableHead>
                <TableHead>Uploaded by</TableHead>
                <TableHead>Added</TableHead>
                <TableHead>Client</TableHead>
                <TableHead className="text-right">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.documents.map((d) => (
                <TableRow key={d.id}>
                  <TableCell className="min-w-48">
                    <a href={apiUrl(`/api/matters/${id}/documents/${d.id}`)} className="font-medium hover:underline" download>
                      {d.name}
                    </a>
                  </TableCell>
                  <TableCell className="figures whitespace-nowrap">{formatBytes(d.size)}</TableCell>
                  <TableCell className="whitespace-nowrap">{uploaderLabel(d.uploadedBy, me)}</TableCell>
                  <TableCell className="figures whitespace-nowrap">{formatDate(d.createdAt)}</TableCell>
                  <TableCell>{d.clientVisible ? <Badge variant="info">Visible</Badge> : <span className="text-muted-foreground">Hidden</span>}</TableCell>
                  <TableCell className="whitespace-nowrap text-right">
                    <Button asChild size="icon" variant="ghost" className="size-8">
                      <a href={apiUrl(`/api/matters/${id}/documents/${d.id}`)} download aria-label={`Download ${d.name}`}>
                        <Download />
                      </a>
                    </Button>
                    <Button size="icon" variant="ghost" className="size-8 text-destructive" aria-label={`Delete ${d.name}`} onClick={() => setDeleting({ id: d.id, name: d.name })}>
                      <Trash2 />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title="Delete this document?"
        description={`${deleting?.name ?? ''} is removed permanently.`}
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={() => deleting && del.mutate(deleting.id)}
      />
    </div>
  );
}
